import { useState } from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { v4 as uuid } from 'uuid';

import { builderSetShouldEditSetup } from '~/actions/builder';
import { workflowUpdateSetup } from '~/actions/workflow';
import { getWorkflowFile, parseTemplate } from '~/api/workflows';
import { MAX_DOCUMENT_NAME_LENGTH } from '~/common/constants/max_lengths';
import { showToast } from '~/components/Shared/EcToast/showToast';
import DescriptionTextAreaForm from '~/components/Workflow/shared/DescriptionTextAreaForm';
import { Button } from '~/eds/src/components/Button/Button';
import { Card } from '~/eds/src/components/Card/Card';
import { Text } from '~/eds/src/components/Text/Text';
import { FeatureFlagType } from '~/enums/FeatureFlagType';
import { FileMimeType } from '~/enums/FileMimeType';
import { WorkflowFieldType } from '~/enums/WorkflowFieldType';
import { WorkflowIntakeFormType } from '~/enums/WorkflowIntakeFormType';
import { WorkflowIntegrationType } from '~/enums/WorkflowIntegrationType';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useAsync } from '~/hooks/useAsync';
import { useHasFeatureFlag } from '~/hooks/useHasFeatureFlag';
import { api } from '~/redux/api/createApi';
import { useRouting } from '~/routing/useRouting';
import { ERROR, SUCCESS } from '~/types/toast.types';
import Box from '~/ui/components/Box/Box';
import CharacterLimit from '~/ui/components/CharacterLimit/CharacterLimit';
import FileExtensionMultiSelect from '~/ui/components/FileExtensionMultiSelect/FileExtensionMultiSelect';
import FileInput from '~/ui/components/FileInput/FileInput';
import FileLabel from '~/ui/components/FileLabel/FileLabel';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import Icon from '~/ui/components/Icon/Icon';
import InputLabel from '~/ui/components/InputLabel/InputLabel';
import LoadingContainer from '~/ui/components/LoadingContainer/LoadingContainer';
import PageLayout from '~/ui/components/PageLayout/PageLayout';
import Switch from '~/ui/components/Switch/Switch';
import TextInput from '~/ui/components/TextInput/TextInput';
import { CHARACTER_INPUT_LIMIT_SHORT } from '~/ui/enums/input';
import { toLowercaseTrimmed } from '~/utils/strings';
import {
  createFieldNameFromPlaceholder,
  createPartiesSigners,
  getFieldIdsByTrimmedKey,
} from '~/utils/workflow';

import Fields from './Fields';
import TemplateSteps from './TemplateSteps';
import { SelectWorkdayProcurementIntegrationType } from './WorkdayProcurementIntegrationControls';

const NAME_LIMIT = CHARACTER_INPUT_LIMIT_SHORT;
const DESCRIPTION_LIMIT = CHARACTER_INPUT_LIMIT_SHORT;

/**
 * Determines whether the workflow is WSS-designated based on its integration type.
 * A workflow is WSS-designated when it has any integration type other than NONE
 * (e.g. PROCUREMENT or WSS_ONLY).
 *
 * @param {import('~/enums/WorkflowIntegrationType').WorkflowIntegrationTypeValue} value - The workflow integration type
 * @returns {boolean} True when the workflow is WSS-designated
 */
function isWssDesignatedFromIntegrationType(value) {
  return value !== WorkflowIntegrationType.NONE;
}

const { Company, CompanyAndCounterparty, Counterparty } =
  WorkflowIntakeFormType;

export function getDisabledState(
  formData,
  description,
  nonUniqueFieldIds,
  isParsingTemplate,
  intl,
) {
  const { name, file, type } = formData;
  let disabled = false;
  let disabledTooltip = '';

  if (!name) {
    disabled = true;
    disabledTooltip = intl.formatMessage({
      id: 'Setup.workflowNameRequired',
      defaultMessage: 'Workflow name is required.',
      description: 'Error message when workflow name is required',
    });
  } else if (name.length > NAME_LIMIT) {
    disabled = true;
    disabledTooltip = intl.formatMessage(
      {
        id: 'Setup.workflowNameTooLong',
        defaultMessage: 'Workflow name exceeded the character limit ({limit}).',
        description: 'Error message when workflow name exceeds character limit',
      },
      { limit: NAME_LIMIT },
    );
  } else if (!type) {
    disabled = true;
    disabledTooltip = intl.formatMessage({
      id: 'Setup.documentOptionRequired',
      defaultMessage: 'At least one document option must be specified.',
      description: 'Error message when no document option is selected',
    });
  } else if ([Company, CompanyAndCounterparty].includes(type) && !file) {
    disabled = true;
    disabledTooltip = intl.formatMessage({
      id: 'Setup.fileRequired',
      defaultMessage:
        'A file must be provided when generating a document from a template.',
      description:
        'Error message when file is required for template generation',
    });
  } else if (description && description.length > DESCRIPTION_LIMIT) {
    disabled = true;
    disabledTooltip = intl.formatMessage(
      {
        id: 'Setup.descriptionTooLong',
        defaultMessage: 'Description exceeded the character limit ({limit}).',
        description: 'Error message when description exceeds character limit',
      },
      { limit: DESCRIPTION_LIMIT },
    );
  } else if (nonUniqueFieldIds.length > 0) {
    disabled = true;
    disabledTooltip = intl.formatMessage({
      id: 'Setup.nonUniqueFieldNamesWarning',
      defaultMessage:
        'We detected document placeholders that share identical names but have different capitalization and/or spacing. Please edit the highlighted placeholders below so that they are identical, including capitalization and spaces, or use different names if they are intended to be different placeholders.',
      description: 'Warning message for non-unique field names',
    });
  } else if (isParsingTemplate) {
    disabled = true;
    disabledTooltip = intl.formatMessage({
      id: 'Setup.templateProcessing',
      defaultMessage: 'Template is being processed.',
      description: 'Message when template is being processed',
    });
  }

  return [disabled, disabledTooltip];
}

function Setup({
  // connected
  workflow,
  builderSetShouldEditSetup,
  workflowUpdateSetup,
}) {
  const intl = useIntl();
  const { navigate, params } = useRouting();

  const { workflowId } = params;

  const {
    description: initialDescription = '',
    fields: initialFields = {},
    name: initialName = '',
    type: initialType = '',
    acceptedFileTypes: initialAcceptedFileTypes = [],
  } = workflow;
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);
  const [hasCompanyPaper, setHasCompanyPaper] = useState(
    [Company, CompanyAndCounterparty].includes(initialType),
  );
  const [hasCounterpartyPaper, setHasCounterpartyPaper] = useState(
    [Counterparty, CompanyAndCounterparty].includes(initialType),
  );
  const [fields, setFields] = useState(initialFields);
  const [file, setFile] = useState(null);
  const [hasUploadedNewFile, setHasUploadedNewFile] = useState(false);
  const [acceptedFileTypes, setAcceptedFileTypes] = useState(
    initialAcceptedFileTypes,
  );
  const hasIntakeFormAllFileTypesFeatureFlag = useHasFeatureFlag(
    FeatureFlagType.IntakeFormAllFileTypes,
  );
  const [WSSDesignatedWorkflow, setWSSDesignatedWorkflow] = useState(() => {
    if (workflow?.integrationType) {
      return isWssDesignatedFromIntegrationType(workflow.integrationType);
    }
    return workflow?.wssDesignated || false;
  });
  const [integrationType, setIntegrationType] = useState(() => {
    if (workflow?.integrationType) {
      return workflow.integrationType;
    }
    if (workflow?.wssDesignated) {
      return WorkflowIntegrationType.PROCUREMENT;
    }
    return WorkflowIntegrationType.NONE;
  });

  const WSSIntegrationWorkflowBuilderIntegrationTypeSelector = useFlag(
    FlagType.WSSIntegrationWorkflowBuilderIntegrationTypeSelector,
  );
  const WSSDesignatedFeatureFlag = useFlag(FlagType.WSSDesignatedWorkflow);
  const { isLoading: isLoadingFile } = useAsync(
    getWorkflowFile,
    { id: workflowId, type: 'original' },
    {
      errorToastMessage: intl.formatMessage({
        id: 'Setup.fetchFileError',
        defaultMessage:
          'An error occurred while fetching file. Please try again.',
        description: 'Error message when fetching file fails',
      }),
      successHandler: setFile,
      deps: [initialType],
      condition: workflowId && initialType !== Counterparty,
    },
  );

  const { executor: executeParseTemplate, isLoading: isParsingTemplate } =
    useAsync(parseTemplate, file, {
      errorHandler: clearFile,
      successHandler: handleParseTemplate,
      successToastMessage: intl.formatMessage({
        id: 'Setup.templateProcessedSuccess',
        defaultMessage: 'Template document successfully processed.',
        description: 'Success message when template is processed',
      }),
      errorToastMessage: intl.formatMessage({
        id: 'Setup.templateProcessedError',
        defaultMessage:
          'Some errors occurred while parsing the template document for placeholders. Please ensure all placeholders begin with "{{" and end with "}}", and they do not contain any special symbols (e.g. #, @)',
        description: 'Error message when template processing fails',
      }),
    });

  let type;
  if (hasCompanyPaper && hasCounterpartyPaper) {
    type = CompanyAndCounterparty;
  } else if (hasCompanyPaper) {
    type = Company;
  } else if (hasCounterpartyPaper) {
    type = Counterparty;
  }

  const formDataWssDesignated =
    WSSIntegrationWorkflowBuilderIntegrationTypeSelector
      ? isWssDesignatedFromIntegrationType(integrationType)
      : WSSDesignatedWorkflow;

  let formDataIntegrationType;
  if (WSSIntegrationWorkflowBuilderIntegrationTypeSelector) {
    formDataIntegrationType = integrationType;
  } else {
    formDataIntegrationType = WSSDesignatedWorkflow
      ? WorkflowIntegrationType.PROCUREMENT
      : WorkflowIntegrationType.NONE;
  }

  const formData = {
    description,
    file,
    name,
    fields,
    type,
    acceptedFileTypes: acceptedFileTypes,
    wssDesignated: formDataWssDesignated,
    integrationType: formDataIntegrationType,
  };

  const fieldIdsByUniquePlaceholder = getFieldIdsByTrimmedKey(
    fields,
    'placeholder',
  );
  const nonUniqueFieldIds = Object.values(fields)
    .filter(
      (field) =>
        fieldIdsByUniquePlaceholder[toLowercaseTrimmed(field.placeholder || '')]
          ?.length > 1,
    )
    .map((field) => field.id);

  const [disabled, disabledTooltip] = getDisabledState(
    formData,
    description,
    nonUniqueFieldIds,
    isParsingTemplate,
    intl,
  );

  function onBack() {
    builderSetShouldEditSetup(false);
  }

  const [createWorkflowMutation, { isLoading: isCreating }] =
    api.endpoints.createWorkflow.useMutation();

  async function executeCreateWorkflow() {
    try {
      const response = await createWorkflowMutation({
        ...formData,
        ...createPartiesSigners(),
      }).unwrap();
      builderSetShouldEditSetup(false);
      navigate(`/workflow/builder/${response.id}`);
      showToast(
        SUCCESS,
        intl.formatMessage(
          {
            id: 'Setup.createWorkflowSuccess',
            defaultMessage: 'Workflow "{name}" has been successfully created.',
            description: 'Success message when workflow is created',
          },
          { name },
        ),
      );
    } catch {
      showToast(
        ERROR,
        intl.formatMessage(
          {
            id: 'Setup.createWorkflowError',
            defaultMessage:
              'Unable to create workflow "{name}", please try again.',
            description: 'Error message when creating workflow fails',
          },
          { name },
        ),
      );
    }
  }

  function handleOnUpdateClick() {
    if (hasUploadedNewFile) {
      workflowUpdateSetup(formData);
    } else {
      const { file: _file, ...restFormData } = formData;
      workflowUpdateSetup(restFormData);
    }
    builderSetShouldEditSetup(false);
  }

  function clearFile() {
    setFile(null);
  }

  function removeFile() {
    clearFile();
    setFields({});
  }

  function handleWssDesignatedChange(nextSwitchValue) {
    if (!WSSIntegrationWorkflowBuilderIntegrationTypeSelector) {
      setWSSDesignatedWorkflow(nextSwitchValue);
      workflowUpdateSetup({
        ...workflow,
        wssDesignated: nextSwitchValue,
        integrationType: nextSwitchValue
          ? WorkflowIntegrationType.PROCUREMENT
          : WorkflowIntegrationType.NONE,
      });
      return;
    }
    if (!nextSwitchValue) {
      setIntegrationType(WorkflowIntegrationType.NONE);
      setWSSDesignatedWorkflow(false);
      workflowUpdateSetup({
        ...workflow,
        wssDesignated: false,
        integrationType: WorkflowIntegrationType.NONE,
      });
      return;
    }
    const nextType =
      integrationType === WorkflowIntegrationType.WSS_ONLY
        ? WorkflowIntegrationType.WSS_ONLY
        : WorkflowIntegrationType.PROCUREMENT;
    setIntegrationType(nextType);
    setWSSDesignatedWorkflow(isWssDesignatedFromIntegrationType(nextType));
    workflowUpdateSetup({
      ...workflow,
      wssDesignated: isWssDesignatedFromIntegrationType(nextType),
      integrationType: nextType,
    });
  }

  function handleIntegrationTypeChange(nextType) {
    setIntegrationType(nextType);
    const wssDesignated = isWssDesignatedFromIntegrationType(nextType);
    setWSSDesignatedWorkflow(wssDesignated);
    workflowUpdateSetup({
      ...workflow,
      wssDesignated,
      integrationType: nextType,
    });
  }

  function handleParseTemplate(parsed) {
    const { conditionals, placeholders } = parsed;

    const newFields = {};
    conditionals.forEach((conditionalText) => {
      const id = uuid();
      newFields[id] = {
        id,
        name: createFieldNameFromPlaceholder(conditionalText.placeholder),
        conditionalText,
        placeholder: conditionalText.placeholder,
        type: WorkflowFieldType.ConditionalText,
      };
    });
    placeholders.forEach((placeholder) => {
      const id = uuid();
      newFields[id] = {
        id,
        name: createFieldNameFromPlaceholder(placeholder),
        placeholder,
      };
    });
    setFields(newFields);
  }

  function validateDocumentName(file) {
    if (file.name.length > MAX_DOCUMENT_NAME_LENGTH) {
      showToast(
        ERROR,
        intl.formatMessage(
          {
            id: 'Setup.fileNameTooLong',
            defaultMessage:
              'Uploaded file name cannot be longer than {maxLength} characters.',
            description: 'Error message when uploaded file name is too long',
          },
          { maxLength: MAX_DOCUMENT_NAME_LENGTH },
        ),
      );
    } else {
      setFile(file);
      executeParseTemplate();
      setHasUploadedNewFile(true);
    }
  }

  let FileContent;
  if (hasCompanyPaper) {
    FileContent = file ? (
      <FlexLayout
        alignItems="center"
        bg="gray-200"
        justifyContent="space-between"
        px={6}
        sx={{ borderRadius: 'm', height: 'input-height' }}
      >
        <FileLabel enableDownload file={file} />
        <Icon color="gray-500" icon="close" size="s" onClick={removeFile} />
      </FlexLayout>
    ) : null;
  }

  return (
    <PageLayout
      header={
        <PageLayout.Header
          leftIcon={{
            icon: 'chevronLeft',
            onClick: onBack,
          }}
          title={{
            color: name ? 'gray-700' : 'gray-500',
            text:
              name ||
              intl.formatMessage({
                id: 'Setup.untitledWorkflow',
                defaultMessage: 'Untitled workflow',
                description: 'Default text for untitled workflow',
              }),
          }}
          rightContent={
            <Icon
              icon="close"
              size="m"
              color="gray-700"
              sx={{ cursor: 'pointer' }}
              onClick={onBack}
            />
          }
        />
      }
    >
      <FlexLayout
        flex="1 1 auto"
        flexDirection="column"
        mx="auto"
        sx={{ maxWidth: '808px' }}
        space={12}
      >
        <FlexLayout flexDirection="column" space={4}>
          <InputLabel
            isRequired
            id="workflow-name"
            label={intl.formatMessage({
              id: 'Setup.workflowNameLabel',
              defaultMessage: 'Workflow name',
              description: 'Label for workflow name input',
            })}
          >
            <TextInput
              autoFocus
              id="workflow--set-up-name"
              value={name}
              placeholder={intl.formatMessage({
                id: 'Setup.workflowNamePlaceholder',
                defaultMessage: 'e.g. Purchase Order or Standard MSA',
                description: 'Placeholder for workflow name input',
              })}
              width="fullWidth"
              onChange={setName}
            />
            <FlexLayout justifyContent="flex-end">
              <CharacterLimit limit={NAME_LIMIT} value={name} />
            </FlexLayout>
          </InputLabel>
          <DescriptionTextAreaForm
            description={description}
            id="workflow--set-up-description"
            noDescriptionMessage={intl.formatMessage({
              id: 'Setup.descriptionPlaceholder',
              defaultMessage: 'Provide a description (optional)...',
              description: 'Placeholder for description input',
            })}
            onUpdate={setDescription}
          />
        </FlexLayout>
        <FlexLayout flexDirection="column" space={3}>
          {WSSDesignatedFeatureFlag && (
            <>
              <Text color="text.primary" variant="body-bold">
                {intl.formatMessage({
                  id: 'Setup.integrationsTitle',
                  defaultMessage: 'Integrations',
                  description: 'Title for integrations section',
                })}
              </Text>
              <SelectWorkdayProcurementIntegrationType
                disabled={Boolean(workflow.id)}
                integrationType={integrationType}
                isWssDesignated={
                  WSSIntegrationWorkflowBuilderIntegrationTypeSelector
                    ? integrationType !== WorkflowIntegrationType.NONE
                    : WSSDesignatedWorkflow
                }
                onChange={handleWssDesignatedChange}
                onIntegrationTypeChange={handleIntegrationTypeChange}
              />
            </>
          )}
          <Text color="text.primary" variant="body-bold">
            {intl.formatMessage({
              id: 'Setup.paperSourceTitle',
              defaultMessage: 'Select paper source',
              description: 'Title for paper source selection',
            })}
          </Text>
          <FlexLayout
            space={2}
            sx={{
              backgroundColor: 'gray-300',
              borderRadius: 'm',
            }}
          >
            <Card>
              <FlexLayout
                alignItems="center"
                justifyContent="space-between"
                space={3}
              >
                <Icon icon="info" size="xs" />
                <Text color="text.secondary" variant="body">
                  {intl.formatMessage({
                    id: 'Setup.paperSourceInfo',
                    defaultMessage:
                      'When both options are selected, the ticket creator will be able to choose if they want to generate or upload a document',
                    description: 'Information about paper source options',
                  })}
                </Text>
              </FlexLayout>
            </Card>
          </FlexLayout>
          <Card>
            <FlexLayout alignItems="center" justifyContent="space-between">
              <FlexLayout
                flexDirection="column"
                space={2}
                sx={{ maxWidth: '600px' }}
              >
                <Text color="text.primary" variant="body-bold">
                  {intl.formatMessage({
                    id: 'Setup.uploadDocumentTitle',
                    defaultMessage:
                      'Users will be able to upload an existing document',
                    description: 'Title for upload document option',
                  })}
                </Text>
                <Text color="text.secondary" variant="body">
                  {intl.formatMessage({
                    id: 'Setup.uploadDocumentDescription',
                    defaultMessage:
                      "The document will be collected from the ticket creator when they submit a new request. You can specify any file types to upload, but only .pdf, .doc and .docx will be processed by Evisort's AI upon ticket completion. ",
                    description: 'Description for upload document option',
                  })}
                </Text>
                {hasIntakeFormAllFileTypesFeatureFlag &&
                  hasCounterpartyPaper && (
                    <FileExtensionMultiSelect
                      onChange={setAcceptedFileTypes}
                      values={acceptedFileTypes}
                      placeholder={intl.formatMessage({
                        id: 'Setup.allowedFileTypesPlaceholder',
                        defaultMessage: 'Add allowed file types',
                        description: 'Placeholder for allowed file types input',
                      })}
                    />
                  )}
              </FlexLayout>
              <Switch
                id="workflow--set-up-has-counterparty-paper"
                value={hasCounterpartyPaper}
                onChange={setHasCounterpartyPaper}
              />
            </FlexLayout>
          </Card>
          <Card>
            <FlexLayout
              alignItems="center"
              justifyContent="space-between"
              space={2}
            >
              <FlexLayout
                flexDirection="column"
                space={2}
                sx={{ maxWidth: '600px' }}
              >
                <Text color="text.primary" variant="body-bold">
                  {intl.formatMessage({
                    id: 'Setup.generateDocumentTitle',
                    defaultMessage:
                      'Users will be able to generate document from a template',
                    description: 'Title for generate document option',
                  })}
                </Text>
                <Text color="text.secondary" variant="body">
                  {intl.formatMessage({
                    id: 'Setup.generateDocumentDescription',
                    defaultMessage:
                      'Upload existing document with marked field and conditional text to be used as a template.',
                    description: 'Description for generate document option',
                  })}
                </Text>
              </FlexLayout>
              <Switch
                id="workflow--set-up-has-company-paper"
                value={hasCompanyPaper}
                onChange={(updatedValue) => {
                  if (!updatedValue) {
                    setFields({});
                  }
                  setHasCompanyPaper(updatedValue);
                  setFile();
                }}
              />
            </FlexLayout>
            <Box bg="gxray-400" my={6} sx={{ height: '1px' }} />
            <LoadingContainer isLoading={isLoadingFile || isParsingTemplate}>
              <FileInput
                className="workflow/builder/setup__upload-company-paper"
                enableUpload={hasCompanyPaper}
                id="workflow--set-up-upload-company-paper-trigger"
                file={file}
                acceptedFiles={FileMimeType.Docx}
                enableDropzone
                onChange={(files) => {
                  validateDocumentName(files[0]);
                }}
              >
                {!file && <TemplateSteps />}
                {FileContent}
                {nonUniqueFieldIds.length > 0 && (
                  <FlexLayout alignItems="center" mt={8} space={3}>
                    <Icon color="yellow-500" icon="warning" size="s" />
                    <Text color="peach-800" variant="2xs-dense">
                      {intl.formatMessage({
                        id: 'Setup.nonUniqueFieldNamesWarning',
                        defaultMessage:
                          'We detected document placeholders that share identical names but have different capitalization and/or spacing. Please edit the highlighted placeholders below so that they are identical, including capitalization and spaces, or use different names if they are intended to be different placeholders.',
                        description:
                          'Warning message for non-unique field names',
                      })}
                    </Text>
                  </FlexLayout>
                )}
                {file && (
                  <Fields
                    fields={fields}
                    nonUniqueFieldIds={nonUniqueFieldIds}
                  />
                )}
              </FileInput>
            </LoadingContainer>
          </Card>
        </FlexLayout>
        <FlexLayout justifyContent="flex-end" space={3}>
          <Button
            text={intl.formatMessage({
              id: 'Setup.cancelButton',
              defaultMessage: 'Cancel',
              description: 'Cancel button text',
            })}
            variant="secondary"
            onClick={onBack}
          />
          {workflowId ? (
            <Button
              tooltip={disabledTooltip}
              disabled={disabled}
              text={intl.formatMessage({
                id: 'Setup.updateButton',
                defaultMessage: 'Update',
                description: 'Update button text',
              })}
              variant="primary"
              onClick={handleOnUpdateClick}
            />
          ) : (
            <Button
              tooltip={disabledTooltip}
              disabled={disabled}
              id="workflow--set-up-continue"
              isLoading={isCreating}
              text={intl.formatMessage({
                id: 'Setup.continueButton',
                defaultMessage: 'Continue',
                description: 'Continue button text',
              })}
              variant="primary"
              onClick={executeCreateWorkflow}
            />
          )}
        </FlexLayout>
      </FlexLayout>
    </PageLayout>
  );
}

function mapStateToProps({ workflow }) {
  return { workflow };
}

export default connect(mapStateToProps, {
  builderSetShouldEditSetup,
  workflowUpdateSetup,
})(Setup);
